'use client';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useEffect, useState } from 'react';

import { CMSLink, CMSLinkType } from '@/components/Link';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/contexts/LocaleContext';
import { Case } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import { CaseCard } from '../CaseCard';
import styles from './CaseSlider.module.scss';

type Props = {
  cards: Case[];
  title?: string;
  link?: CMSLinkType;
  trackingCTA?: string;
};

const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: {
    y: 50,
  },
  visible: {
    y: 0,
  },
  exit: {
    y: 50,
  },
};

export const CaseSlider: React.FC<Props> = ({
  cards,
  link,
  title,
  trackingCTA,
}) => {
  const locale = useLocale();
  const t = getDictionary(locale);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      dragThreshold: 5,
      slidesToScroll: 1,
      containScroll: 'keepSnaps',
    },
    [WheelGesturesPlugin()]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const updateScrollState = () => {
      const newIndex = emblaApi.selectedScrollSnap();

      // If the new index is the last slide, we cannot scroll next
      setCanScrollPrev(newIndex > 0);

      // If the new index is the first slide, we cannot scroll previous
      setCanScrollNext(newIndex < emblaApi.scrollSnapList().length - 1);
    };

    const updateSelectedIndex = () => {
      // Debounce this by 600ms
      setTimeout(() => {
        const newIndex = emblaApi.selectedScrollSnap();
        setSelectedIndex(newIndex);
      }, 600);
    };

    emblaApi.on('select', updateScrollState);
    emblaApi.on('select', updateSelectedIndex);
    updateScrollState();

    return () => {
      emblaApi.off('select', updateScrollState);
      emblaApi.off('select', updateSelectedIndex);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <div className={'container'}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <span className={styles.name}>{t('cases.name')}</span>
          {title && <div className={cn(styles.title, 'h4')}>{title}</div>}
          {link && (
            <CMSLink
              {...link}
              appearance='level-4'
              className={cn(styles.link, 'button--small')}
              tracking={{
                type: 'Cases Slider',
                headline: trackingCTA || title || '',
                cta: t('cases.viewAll'),
              }}
            >
              {t('cases.viewAll')}
            </CMSLink>
          )}
        </div>
        <div className={styles.listControls}>
          <Button
            className='button button--medium button--level-2 button--icon'
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            aria-label={t('previous')}
          >
            <span className={styles.icon}>
              <svg
                width='14'
                height='15'
                viewBox='0 0 14 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M9.05988 11.93C9.10052 11.9706 9.13277 12.0189 9.15477 12.072C9.17676 12.1251 9.18809 12.182 9.18809 12.2395C9.18809 12.297 9.17676 12.3539 9.15477 12.407C9.13277 12.4601 9.10052 12.5084 9.05988 12.549C9.01923 12.5897 8.97097 12.6219 8.91786 12.6439C8.86475 12.6659 8.80783 12.6772 8.75034 12.6772C8.69286 12.6772 8.63594 12.6659 8.58283 12.6439C8.52972 12.6219 8.48146 12.5897 8.44081 12.549L4.06581 8.17403C4.02514 8.1334 3.99287 8.08515 3.97085 8.03204C3.94883 7.97893 3.9375 7.922 3.9375 7.8645C3.9375 7.80701 3.94883 7.75008 3.97085 7.69696C3.99287 7.64385 4.02514 7.5956 4.06581 7.55497L8.44081 3.17997C8.52291 3.09788 8.63425 3.05176 8.75034 3.05176C8.86644 3.05176 8.97778 3.09788 9.05988 3.17997C9.14197 3.26206 9.18809 3.3734 9.18809 3.4895C9.18809 3.6056 9.14197 3.71694 9.05988 3.79903L4.99386 7.8645L9.05988 11.93Z' />
              </svg>
            </span>
          </Button>
          <Button
            className='button button--medium button--level-2 button--icon'
            onClick={scrollNext}
            disabled={!canScrollNext}
            aria-label={t('next')}
          >
            <span className={styles.icon}>
              <svg
                width='14'
                height='15'
                viewBox='0 0 14 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M9.93477 8.17403L5.55977 12.549C5.51913 12.5897 5.47087 12.6219 5.41776 12.6439C5.36465 12.6659 5.30773 12.6772 5.25024 12.6772C5.19276 12.6772 5.13584 12.6659 5.08273 12.6439C5.02962 12.6219 4.98136 12.5897 4.94071 12.549C4.90006 12.5084 4.86782 12.4601 4.84582 12.407C4.82382 12.3539 4.8125 12.297 4.8125 12.2395C4.8125 12.182 4.82382 12.1251 4.84582 12.072C4.86782 12.0189 4.90006 11.9706 4.94071 11.93L9.00673 7.8645L4.94071 3.79903C4.85862 3.71694 4.8125 3.6056 4.8125 3.4895C4.8125 3.3734 4.85862 3.26206 4.94071 3.17997C5.0228 3.09788 5.13415 3.05176 5.25024 3.05176C5.36634 3.05176 5.47768 3.09788 5.55977 3.17997L9.93477 7.55497C9.97545 7.5956 10.0077 7.64385 10.0297 7.69696C10.0518 7.75008 10.0631 7.80701 10.0631 7.8645C10.0631 7.922 10.0518 7.97893 10.0297 8.03204C10.0077 8.08515 9.97545 8.1334 9.93477 8.17403Z' />
              </svg>
            </span>
          </Button>
        </div>
      </div>
      <div ref={emblaRef}>
        <motion.div
          variants={listVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className={cn(styles.list)}
        >
          <AnimatePresence>
            {cards.map((card, index) => (
              <motion.div
                key={`CaseSliderCard-${card.id}`}
                variants={cardVariants}
                style={{ willChange: 'transform, opacity' }}
                transition={{ duration: 0.3 }}
                className={cn(styles.slide, {
                  [styles.active]: index === selectedIndex,
                })}
              >
                <CaseCard
                  {...card}
                  aspectRatio='square'
                  slider={true}
                  activeSlide={index === selectedIndex}
                  disableParallax
                  className={cn(styles.card, 'case-card')}
                  tracking={{
                    type: 'Cases Slider',
                    headline: trackingCTA || title || '',
                    cta: card.title,
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      <div className={styles.footer}>
        <div className={styles.listControls}>
          <Button
            className='button button--medium button--level-2 button--icon'
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            aria-label={t('previous')}
          >
            <span className={styles.icon}>
              <svg
                width='14'
                height='15'
                viewBox='0 0 14 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M9.05988 11.93C9.10052 11.9706 9.13277 12.0189 9.15477 12.072C9.17676 12.1251 9.18809 12.182 9.18809 12.2395C9.18809 12.297 9.17676 12.3539 9.15477 12.407C9.13277 12.4601 9.10052 12.5084 9.05988 12.549C9.01923 12.5897 8.97097 12.6219 8.91786 12.6439C8.86475 12.6659 8.80783 12.6772 8.75034 12.6772C8.69286 12.6772 8.63594 12.6659 8.58283 12.6439C8.52972 12.6219 8.48146 12.5897 8.44081 12.549L4.06581 8.17403C4.02514 8.1334 3.99287 8.08515 3.97085 8.03204C3.94883 7.97893 3.9375 7.922 3.9375 7.8645C3.9375 7.80701 3.94883 7.75008 3.97085 7.69696C3.99287 7.64385 4.02514 7.5956 4.06581 7.55497L8.44081 3.17997C8.52291 3.09788 8.63425 3.05176 8.75034 3.05176C8.86644 3.05176 8.97778 3.09788 9.05988 3.17997C9.14197 3.26206 9.18809 3.3734 9.18809 3.4895C9.18809 3.6056 9.14197 3.71694 9.05988 3.79903L4.99386 7.8645L9.05988 11.93Z' />
              </svg>
            </span>
          </Button>
          <Button
            className='button button--medium button--level-2 button--icon'
            onClick={scrollNext}
            disabled={!canScrollNext}
            aria-label={t('next')}
          >
            <span className={styles.icon}>
              <svg
                width='14'
                height='15'
                viewBox='0 0 14 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M9.93477 8.17403L5.55977 12.549C5.51913 12.5897 5.47087 12.6219 5.41776 12.6439C5.36465 12.6659 5.30773 12.6772 5.25024 12.6772C5.19276 12.6772 5.13584 12.6659 5.08273 12.6439C5.02962 12.6219 4.98136 12.5897 4.94071 12.549C4.90006 12.5084 4.86782 12.4601 4.84582 12.407C4.82382 12.3539 4.8125 12.297 4.8125 12.2395C4.8125 12.182 4.82382 12.1251 4.84582 12.072C4.86782 12.0189 4.90006 11.9706 4.94071 11.93L9.00673 7.8645L4.94071 3.79903C4.85862 3.71694 4.8125 3.6056 4.8125 3.4895C4.8125 3.3734 4.85862 3.26206 4.94071 3.17997C5.0228 3.09788 5.13415 3.05176 5.25024 3.05176C5.36634 3.05176 5.47768 3.09788 5.55977 3.17997L9.93477 7.55497C9.97545 7.5956 10.0077 7.64385 10.0297 7.69696C10.0518 7.75008 10.0631 7.80701 10.0631 7.8645C10.0631 7.922 10.0518 7.97893 10.0297 8.03204C10.0077 8.08515 9.97545 8.1334 9.93477 8.17403Z' />
              </svg>
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
